import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Button } from 'reactstrap';
import AuthApi from '../../services/Auth';
import PasswordInput, { usePasswordStrength } from '../../components/PasswordInput';

import logo from '../../assets/images/Kronos.svg';
import './Login.css';

const LoginReset = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tokenFromUrl    = searchParams.get('token') || '';

    const [password, setPassword]         = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [passwordUserInputs, setPasswordUserInputs] = useState([]);
    const [loading, setLoading]           = useState(false);
    const [error, setError]               = useState('');

    useEffect(() => {
        if (!tokenFromUrl) {
            setPasswordUserInputs([]);
            return;
        }

        let cancelled = false;
        AuthApi.getResetPasswordContext(tokenFromUrl)
            .then((userInputs) => {
                if (!cancelled) {
                    setPasswordUserInputs(userInputs);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPasswordUserInputs([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [tokenFromUrl]);

    const passwordStrength = usePasswordStrength(password, {
        language: i18n.language,
        userInputs: passwordUserInputs,
    });

    const canSubmit =
        password !== '' &&
        passwordConfirm !== '' &&
        password === passwordConfirm &&
        passwordStrength.acceptable &&
        !loading;

    const submit = (e) => {
        e.preventDefault();
        if (!canSubmit) {
            setError(t('password.too_weak'));
            return;
        }
        setLoading(true);
        setError('');
        AuthApi.resetPassword(tokenFromUrl, password, passwordConfirm)
            .then((success) => {
                setPassword('');
                setPasswordConfirm('');
                navigate('/login', {
                    replace: true,
                    state: {
                        resetPasswordSuccess: true,
                        resetSuccessMessage:
                            typeof success === 'string' ? success : t('login.reset_success'),
                    },
                });
            })
            .catch((err) => {
                setError(err.message || t('login.generic_error'));
            })
            .finally(() => setLoading(false));
    };

    if (!tokenFromUrl) {
        return (
            <div className="LoginForm appear text-start">
                <img className="logo" src={logo} alt="logo" />
                <div className="alert alert-warning mt-3" role="alert">
                    {t('login.reset_missing_token')}
                </div>
                <p>
                    <Link to="/login">{t('login.back_to_login')}</Link>
                </p>
            </div>
        );
    }

    return (
        <Form onSubmit={submit} className="LoginForm appear">
            <img className="logo" src={logo} alt="logo" />
            <h2 className="h4 mb-3">{t('login.reset_password_title')}</h2>
            <PasswordInput
                required
                id="reset-password"
                name="password"
                placeholder={t('login.new_password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                userInputs={passwordUserInputs}
            />
            <PasswordInput
                required
                id="reset-password-confirmation"
                name="password-confirmation"
                placeholder={t('register.password-confirmation')}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                showStrength={false}
                className="mt-3"
            />
            {passwordConfirm !== '' && password !== passwordConfirm && (
                <div className="text-danger mt-2">{t('password.mismatch')}</div>
            )}
            <Button type="submit" className="btn btn-lg btn-block btn-dark mt-3" disabled={!canSubmit}>
                {loading ? <i className="fa fa-spinner fa-spin" aria-hidden="true" /> : t('login.save_new_password')}
            </Button>
            {error && (
                <div className="alert alert-danger mt-3" role="alert">
                    {error}
                </div>
            )}
            <p className="mt-3">
                <Link to="/login">{t('login.back_to_login')}</Link>
            </p>
        </Form>
    );
};

export default LoginReset;
